import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import { useNavigate } from "react-router-dom";
import { useInterests, useUser } from "@/contexts";
import {
  UserCardData,
  UserCard,
  PremiumAccessModal,
  ModalAlert,
  PremiumFloating,
  WaitingApproval,
} from "@/components";
import { chatService, feedService, interestsService } from "@/services";
import { useCardLock, usePermissions, usePresenceMap } from "@/hooks";
import { slugify } from "@/utils";
import "./Feed.css";

const FeedScreen: React.FC = () => {
  const { userData, user: currentUser, excludedUserIds } = useUser();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { favoritesGiven } = useInterests();
  const presenceMap = usePresenceMap();

  const { isCardLocked } = useCardLock(userData);

  const favoritesSet = useMemo(() => {
    return new Set(favoritesGiven?.map((f) => f.targetUser));
  }, [favoritesGiven]);

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const currentGenderRef = useRef<string | null>(null);

  const isFemale = userData?.gender === "Feminino";
  const isFemaleIsVerified = userData?.isVerified;

  const isShowPremiumAccessModal = !!(isFemale && !isFemaleIsVerified);

  const [cards, setCards] = useState<UserCardData[]>([]);
  const cardsRef = useRef<UserCardData[]>([]);

  const [favoriteBusyId, setFavoriteBusyId] = useState<string | null>(null);
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);
  const [lastVisible, setLastVisible] = useState<any>(null);
  const [isFetching, setIsFetching] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const [showPremiumAccessModal, setShowPremiumAccessModal] = useState(
    isShowPremiumAccessModal,
  );

  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState<string>("");

  const handleRequestChat = async (targetUser: UserCardData) => {
    if (!userData || !currentUser) return;

    const isSubscriber = hasPermission("ACCESS_CHAT_AREA");

    if (!isSubscriber) {
      setAlertMessage("Você precisa ser assinante para enviar mensagens.");
      setIsAlertOpen(true);
      return;
    }

    try {
      const chatId = await chatService.createOrGetConversation(
        currentUser.uid,
        userData,
        targetUser.uid,
        targetUser,
        isSubscriber,
      );

      navigate("/mensagens", { state: { selectedChatId: chatId } });
    } catch (error) {
      console.error("Erro ao iniciar conversa:", error);
    }
  };

  const handleFavorite = useCallback((user: UserCardData) => {
    handleToggleFavorite(user);
  }, []);

  const handleToggleFavorite = async (targetUser: UserCardData) => {
    if (!currentUser || !userData || !targetUser.uid) return;

    const isSubscriber = hasPermission("LIKE_PROFILES");

    if (!isSubscriber) {
      setAlertMessage("Ops! Você precisa ser assinante para favoritar perfis.");
      setIsAlertOpen(true);
      return;
    }

    setFavoriteBusyId(targetUser.uid);

    try {
      await interestsService.toggleFavorite(
        currentUser,
        userData,
        targetUser,
        isSubscriber,
      );
    } catch (e) {
      console.error(e);
    } finally {
      setFavoriteBusyId(null);
    }
  };

  useEffect(() => {
    cardsRef.current = cards;
  }, [cards]);

  const fetchProfiles = useCallback(
    async (isFirstLoad = false) => {
      if (isFetching || (!isFirstLoad && !hasMore)) return;
      if (!userData?.uid || !userData?.gender) return;

      setIsFetching(true);
      try {
        const currentExcludedIds = isFirstLoad
          ? []
          : cardsRef.current.map((c) => c.uid);

        // 1. Busca pessoas próximas pelo Geohash (Raio de 50km)
        let result = await feedService.getDiscoveryFeed({
          uid: userData.uid,
          gender: userData.gender,
          pageSize: 15,
          userLocation: userData.location?.geopoint,
          excludeIds: currentExcludedIds,
          distanceKm: 50,
        });

        let newProfiles = (result.profiles || []) as UserCardData[];

        // 2. SMART FALLBACK: Se a região local não atingiu o limite da página (15),
        // busca o restante no banco sem filtro geográfico
        if (newProfiles.length < 15) {
          const needed = 15 - newProfiles.length;

          const fallbackResult = await feedService.getDiscoveryFeed({
            uid: userData.uid,
            gender: userData.gender,
            pageSize: needed,
            userLocation: undefined, // Força a entrada no bloco else do backend
            excludeIds: [
              ...currentExcludedIds,
              ...newProfiles.map((p) => p.uid),
            ],
          });

          if (fallbackResult.profiles?.length) {
            newProfiles = [...newProfiles, ...fallbackResult.profiles];
          }
        }

        // 3. Atualização de estado e controle de paginação real
        if (newProfiles.length === 0) {
          setHasMore(false);
        } else {
          setCards((prev) =>
            isFirstLoad
              ? newProfiles
              : [
                  ...new Map(
                    [...prev, ...newProfiles].map((item) => [item.uid, item]),
                  ).values(),
                ],
          );

          setLastVisible(null);

          // Se o total retornado (locais + globais) for menor que o requisitado, o banco esgotou
          if (newProfiles.length < 15) {
            setHasMore(false);
          }
        }
      } catch (error) {
        console.error("Erro no Feed:", error);
        setHasMore(false);
      } finally {
        setIsFetching(false);
      }
    },
    [
      userData?.uid,
      userData?.gender,
      userData?.location?.geopoint,
      isFetching,
      hasMore,
    ],
  );

  useEffect(() => {
    if (currentGenderRef.current === userData?.gender) return;

    currentGenderRef.current = userData?.gender || null;

    setCards([]);
    setLastVisible(null);
    setHasMore(true);

    fetchProfiles(true);
  }, [userData?.gender]);

  useEffect(() => {
    if (!excludedUserIds || excludedUserIds.length === 0) return;
    const excluded = new Set(excludedUserIds);
    setCards((prev) => prev.filter((c) => !excluded.has(c.uid)));
  }, [excludedUserIds]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isFetching) {
          fetchProfiles();
        }
      },
      { rootMargin: "200px" },
    );

    if (sentinelRef.current) observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [fetchProfiles, hasMore, isFetching]);

  if (userData?.awaitingApproval) {
    return (
      <div className="feed-page container">
        <WaitingApproval username={userData.username || ""} />
      </div>
    );
  }

  return (
    <div className=" container">
      <div className="feed-header">
        <div>
          <p className="feed-kicker">Destaques perto de você</p>
          <h1 className="feed-title">Explore perfis e encontre seu match</h1>
        </div>
      </div>

      {/* <div className="feed-filter-bar container">
        <div className="filter-left-placeholder">
        </div>

        <div className="filter-sort-wrapper">
          <span className="sort-label">Ordenar por</span>
          <select className="sort-select">
            <option value="relevantes">Mais relevantes</option>
            <option value="perto">Mais perto</option>
          </select>
        </div>
      </div> */}

      <div className="feed-grid ">
        {cards.map((card, index) => (
          <UserCard
            key={`${card.uid}-${index}`}
            data={card}
            isLocked={isCardLocked(card)}
            isFavorited={favoritesSet.has(card.uid)}
            presence={presenceMap[card.uid]}
            selected={selectedCardId === card.uid}
            favoriteBusyId={favoriteBusyId}
            onNavigate={() => {
              navigate("/perfil/" + slugify(card.username));
            }}
            onSelect={(uid) => {
              setSelectedCardId((prev) => (prev === uid ? null : uid));
            }}
            onStartChat={handleRequestChat}
            onFavorite={handleFavorite}
          />
        ))}
      </div>

      <div
        ref={sentinelRef}
        style={{ height: "20px", background: "transparent" }}
      />

      {isFetching && (
        <div className="feed-loading">Buscando novos rostos...</div>
      )}

      {!hasMore && cards.length > 0 && (
        <div className="feed-end">
          <p>Você chegou ao fim!</p>
        </div>
      )}

      {!isFetching && cards.length === 0 && (
        <div className="feed-no-results">
          <p>Nenhum perfil encontrado.</p>
          <small>
            Verifique se existem usuários cadastrados com esse gênero.
          </small>
        </div>
      )}

      {!userData?.planInfo?.active && (
        <PremiumFloating navigate={() => navigate("/planos")} />
      )}

      <PremiumAccessModal
        isOpen={showPremiumAccessModal}
        onClose={() => setShowPremiumAccessModal(false)}
      />

      <ModalAlert
        title={alertMessage || "Ops!"}
        isOpen={isAlertOpen}
        onClose={() => setIsAlertOpen(false)}
        onSubscribe={() => {
          navigate("/planos");
          setIsAlertOpen(false);
        }}
      />
    </div>
  );
};

export default FeedScreen;
